import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";

import AmountField from "../../Components/Form Fields/amount-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import FormSwitch from "../../Components/Form Fields/switch";

import renderAuthorizeTransfer from "../../Organisms/Modals/Transfer/authorize-transfer";
import renderStatusModal from "../../Organisms/Modals/status-modal";
import renderAstraSignUp from "../../Organisms/Modals/Cards/astra-sign-up";
import renderMandateBankAccountAddition from "../../Organisms/Modals/Bank Account/mandate-bank-account-addition";
import renderGetRecurringDepositPreferences from "../../Organisms/Modals/Recurring Deposits/get-recurring-deposit-preferences";
import renderInstantTransferIneligible from "../../Organisms/Modals/Cards/instant-transfer-ineligible";
import renderInstantTransferLimitReached from "../../Organisms/Modals/Cards/instant-transfer-limit-reached";
import renderSelectConnectedAccounts from "../../Organisms/Modals/Bank Account/select-connected-accounts";
import renderSelectDebitCards from "../../Organisms/Modals/Cards/select-debit-cards";

import useAstra from "../../CustomHooks/useAstra";

import withFormProvider from "../../HOC/withFormProvider";

import {
  useConnectedAccounts,
  useConnectedAccountSelection,
  useStakeDebit,
  useDeals,
  useGetRecurringDeposit,
  useBankingLimits,
} from "../../CustomHooks/Queries";
import {
  useInitiateACHDebit,
  useCreateTransferRoutine,
  useActivateRecurringDeposit,
} from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import { getTransferRoutinePayload } from "../../Utils/api-payload-generators";
import {
  getSelectedAstraCard,
  getActiveSVDAstraDebitCard,
  dispatchErrorToast,
  formatCurrency,
  getTransferTypes,
  isSVDApproved,
  isSelectedTransferMethodInstant,
  isSelectedTransferMethodBank,
  getAstraCardsWithSystemCardFiltered,
  formatDate,
  appendISOTime,
} from "../../Utils/helpers";
import { getLastFour } from "../../Utils/pii";
import {
  FailedRecurringDepositStatusModalProps,
  FailedTransferStatusModalProps,
  nilYearLongMonthDateFormat,
  SuccessfulRecurringDepositStatusModalProps,
  SuccessfulTransferStatusModalProps,
} from "../../Utils/constants";

import { ArrowLeftCircle, ChevronRight, Edit } from "../../Assets/Icons";

function AddCash() {
  const recurringDepositAttributesRef = useRef(null);

  const modalContext = useModalContext();

  const { updateModalOnCloseCallBack, onModalClose, popModalStack } = modalContext;

  const navigate = useNavigate();

  const { appUtilityProps, setAppUtilityProps } = useAppUtilityContext();

  const [selectedTransferType, setSelectedTransferType] = useState(appUtilityProps?.preferredTransferType ?? "ach");

  const { watch, handleSubmit } = useFormContext();

  const [isRecurringTransaction, setIsRecurringTransaction] = useState(false);

  const {
    isCreatingUserIntent: isAstraCreatingUserIntent,
    isLoadingRoutesCapabilities,
    isCreatingTrustedAuthenticationToken,
    isLoadingAstraCards,
    isInstantTransferIneligible,
    openAstraPortal,
    astraDebitCards,
  } = useAstra();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();
  
  const { isLoading: isLoadingBankingLimits, data: bankingLimits } = useBankingLimits();

  const { isLoading: isLoadingConnectedAccounts, data: connectedAccounts } = useConnectedAccounts();

  const { isLoading: isLoadingConnectedAccountSelection, data: connectedAccountSelection } =
    useConnectedAccountSelection();

  const {
    isLoading: isLoadingRecurringDeposit,
    data: recurringDeposit,
    refetch: refetchRecurringDeposit,
  } = useGetRecurringDeposit();

  const isAstraAccountConnected = useMemo(
    () => getAstraCardsWithSystemCardFiltered(astraDebitCards, stakeDebit).length > 0,
    [astraDebitCards, stakeDebit],
  );

  const isBankAccountConnected = useMemo(() => {
    return connectedAccounts?.data?.data?.accounts?.length > 0;
  }, [connectedAccounts?.data?.data?.accounts]);

  const selectedAccount = useMemo(
    () =>
      connectedAccounts?.data?.data?.accounts?.find(
        (account) =>
          account.account_id === appUtilityProps?.selectedAccountId ?? connectedAccountSelection?.data?.data?.selected,
      ) ??
      connectedAccounts?.data?.data?.accounts?.[0] ??
      null,
    [
      appUtilityProps?.selectedAccountId,
      connectedAccounts?.data?.data?.accounts,
      connectedAccountSelection?.data?.data?.selected,
    ],
  );

  const SVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const dailyAddFundsLimit = useMemo(() => {
    const dailyLimit = (bankingLimits?.daily_add_cash_limit ?? 0) / 100;
    return dailyLimit;
  }, [bankingLimits]);

  const selectedAstraCard = useMemo(
    () => getSelectedAstraCard(astraDebitCards, stakeDebit, appUtilityProps?.selectedCardId),
    [astraDebitCards, stakeDebit, appUtilityProps?.selectedCardId],
  );

  const accountDescription = useMemo(() => {
    return !!selectedAccount ? `${selectedAccount?.name} (...${selectedAccount?.mask})` : "";
  }, [selectedAccount]);

  const instantAccountDescription = useMemo(() => {
    return !!selectedAstraCard ? `${selectedAstraCard?.card_company} (...${selectedAstraCard?.last_four_digits})` : "";
  }, [selectedAstraCard]);

  const transferTypes = useMemo(() => getTransferTypes(deals?.data), [deals]);

  useEffect(() => {
    if (!isLoadingStakeDebit && !SVDApproved) {
      navigate("/banking");
    }
  }, [isLoadingStakeDebit, SVDApproved, navigate]);

  const GoToBanking = () => navigate("/banking");

  const onAddCashSuccess = (description) => {
    updateModalOnCloseCallBack(GoToBanking);
    renderStatusModal(modalContext, {
      ...SuccessfulTransferStatusModalProps,
      description,
      onCTAButtonClick: () => onModalClose({ callOnClose: GoToBanking }),
    });
  };

  const onAddCashError = (errorProps = {}) =>
    renderStatusModal(modalContext, {
      ...FailedTransferStatusModalProps,
      ...errorProps,
      onCTAButtonClick: () => onModalClose(),
    });

  const { mutate: InitiateACHDebit } = useInitiateACHDebit({
    onSuccess: () =>
      onAddCashSuccess(`Your transfer is expected to arrive on ${deals?.data?.content?.transaction_clearing_date}.`),
    onError: (err) => onAddCashError(err?.response?.status === 422 ? { description: err?.response?.data?.error } : {}),
  });

  const { mutate: CreateTransferRoutine } = useCreateTransferRoutine({
    onSuccess: () => onAddCashSuccess("Your transfer is expected to arrive in a few minutes."),
    onError: (err) => {
      if (err?.response?.status === 429) renderInstantTransferLimitReached(modalContext);
      else onAddCashError();
    },
  });

  const { mutate: ActivateRecurringDeposit } = useActivateRecurringDeposit({
    onSuccess: (_, v) => {
      updateModalOnCloseCallBack(GoToBanking);
      renderStatusModal(modalContext, {
        ...SuccessfulRecurringDepositStatusModalProps,
        description: `Your first deposit is scheduled for ${formatDate(
          appendISOTime(v.start_date),
          nilYearLongMonthDateFormat,
        )}.`,
        onCTAButtonClick: () => onModalClose({ callOnClose: () => navigate("/banking/recurring-deposits") }),
        onSecondaryCTAButtonClick: () => onModalClose({ callOnClose: GoToBanking }),
      });
      refetchRecurringDeposit();
    },
    onError: () =>
      renderStatusModal(modalContext, {
        ...FailedRecurringDepositStatusModalProps,
        onCTAButtonClick: () => popModalStack(0),
        onSecondaryCTAButtonClick: () => onModalClose(),
      }),
  });

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");

  const disableSubmitButton = isNaN(cashAmount) || cashAmount === 0;

  useEffect(() => {
    if (isSelectedTransferMethodInstant(selectedTransferType) && isInstantTransferIneligible) {
      setSelectedTransferType("ach");
    }
  }, [selectedTransferType, isInstantTransferIneligible]);

  const openAuthorizeTransferModal = () => {
    renderAuthorizeTransfer(modalContext, {
      stake_account_number_last4: getLastFour(stakeDebit?.data?.data?.account_number),
      stake_routing_number: stakeDebit?.data?.data?.routing_number,
      ...(isSelectedTransferMethodInstant(selectedTransferType) ? { selectedAstraCard } : { selectedAccount }),
      clearingTransactionDate: deals?.data?.content?.transaction_clearing_date,
      amount: cashAmount,
      isRecurringDeposit: isRecurringTransaction,
      recurringDepositPreferences: recurringDepositAttributesRef.current?.preferences,
      AuthorizeCallback: () => {
        if (isSelectedTransferMethodInstant(selectedTransferType)) {
          const svdAstraCard = getActiveSVDAstraDebitCard(astraDebitCards, stakeDebit);
          CreateTransferRoutine(
            getTransferRoutinePayload({
              sourceCard: svdAstraCard,
              destinationCard: selectedAstraCard,
              amount: cashAmount,
            }),
          );
        } else {
          if (isRecurringTransaction) {
            ActivateRecurringDeposit(recurringDepositAttributesRef.current?.preferences);
          } else {
            InitiateACHDebit({
              accountId: selectedAccount.account_id,
              amount: cashAmount,
            });
          }
        }
      },
    });
  };

  const onSubmit = () => {
    if (isRecurringTransaction) {
      recurringDepositAttributesRef.current = {
        preferences: {
          ...(recurringDepositAttributesRef.current.preferences ?? {}),
          amount: cashAmount,
          originating_account_id: selectedAccount.account_id,
        },
      };
    }

    if (
      (isSelectedTransferMethodBank(selectedTransferType) && !selectedAccount) ||
      (isSelectedTransferMethodInstant(selectedTransferType) && !selectedAstraCard)
    ) {
      dispatchErrorToast(
        isSelectedTransferMethodInstant(selectedTransferType)
          ? "Please add a debit card to proceed"
          : "Please add a bank account to proceed",
        {
          alignToAppContent: true,
          key: isSelectedTransferMethodInstant(selectedTransferType) ? "noAstraDebitCard" : "noConnectedAccount",
        },
      );
    } else if (dailyAddFundsLimit > 0 && cashAmount > dailyAddFundsLimit) {
      dispatchErrorToast("Amount cannot exceed the daily limit.", {
        alignToAppContent: true,
        key: "amountExceedsThreshold",
      });
    } else {
      openAuthorizeTransferModal();
    }
  };

  const handleChangeTransferType = (selectedType) => {
    if (isSelectedTransferMethodInstant(selectedType) && isInstantTransferIneligible) {
      renderInstantTransferIneligible(modalContext);
      return;
    }

    if (isSelectedTransferMethodInstant(selectedType) && !isAstraAccountConnected) {
      renderAstraSignUp(modalContext, openAstraPortal);
    }

    if (isSelectedTransferMethodInstant(selectedType) && isRecurringTransaction) {
      setIsRecurringTransaction(false);
    }

    setSelectedTransferType(selectedType);
    setAppUtilityProps((p) => ({
      ...p,
      preferredTransferType: selectedType,
    }));
  };

  const handleSelectPaymentEntity = () => {
    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      renderSelectDebitCards(modalContext);
    } else {
      renderSelectConnectedAccounts(modalContext);
    }
  };

  const openGetRecurringDepositPreferencesModal = () =>
    renderGetRecurringDepositPreferences(modalContext, {
      onSubmit: ({ startDate, frequency }) => {
        recurringDepositAttributesRef.current = {
          preferences: {
            frequency_type: frequency,
            start_date: startDate,
          },
        };
        setIsRecurringTransaction(true);
        onModalClose();
      },
    });

  const handleToggleRecurring = () => {
    if (!isBankAccountConnected) {
      renderMandateBankAccountAddition(modalContext, {
        title: "Bank Account Required",
        description:
          "To set up a recurring deposit, you must first connect a bank account. Please add your bank account to continue.",
        onSuccess: openGetRecurringDepositPreferencesModal,
      });
    } else if (!isRecurringTransaction) {
      openGetRecurringDepositPreferencesModal();
    } else {
      setIsRecurringTransaction((p) => !p);
    }
  };

  const feeLabel = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_OUT_OF_STAKE_FEE_RATE ?? 0.0175;

    if (isSelectedTransferMethodInstant(selectedTransferType) && isNaN(cashAmount)) {
      return `${(feePercentage * 100).toFixed(2)}%`;
    } else if (isSelectedTransferMethodInstant(selectedTransferType)) {
      return formatCurrency(cashAmount * feePercentage);
    } else {
      return "Free";
    }
  }, [deals, cashAmount, selectedTransferType]);

  const cashAmountIncludingFees = useMemo(() => {
    const feePercentage = deals?.data?.data?.currentDeal?.INSTANT_TRANSFER_INTO_STAKE_FEE_RATE ?? 0;

    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      if (isNaN(cashAmount)) {
        return 0;
      }

      return cashAmount + cashAmount * feePercentage;
    } else {
      return cashAmount;
    }
  }, [cashAmount, selectedTransferType, deals]);

  const transferAmountLabel = useMemo(() => {
    if (isSelectedTransferMethodInstant(selectedTransferType)) {
      return `Add Cash ${formatCurrency(cashAmountIncludingFees)}`;
    } else {
      return isRecurringTransaction ? "Schedule" : "Add Cash";
    }
  }, [selectedTransferType, isRecurringTransaction, cashAmountIncludingFees]);

  const isLoading =
    isLoadingDeals ||
    isLoadingStakeDebit ||
    isLoadingConnectedAccounts ||
    isLoadingConnectedAccountSelection ||
    isLoadingAstraCards ||
    isLoadingRecurringDeposit ||
    isLoadingBankingLimits;

  const isSettingUpAstra =
    isAstraCreatingUserIntent || isLoadingRoutesCapabilities || isCreatingTrustedAuthenticationToken;

  return (
    <div className="add-cash">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={() => navigate(-1)}>
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">Add Cash</h3>
              <div className="dummy"></div>
            </div>
            {isLoading ? (
              <div className="loading-content">
                <CircularProgress />
              </div>
            ) : (
              <form className="content mt-sm-12 mt-6" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div className="top-container">
                  <AmountField />
                  {!!dailyAddFundsLimit && (
                    <span className="amount-usage-label mt-2">Daily limit: {formatCurrency(dailyAddFundsLimit)}</span>
                  )}
                  {isSelectedTransferMethodInstant(selectedTransferType) && (
                    <span className="max-no-of-transfers-badge mt-2">Max Transfer Limit: 2 per day</span>
                  )}
                </div>
                <div className="bottom-container">
                  <div className="transfer-types mb-6">
                    {transferTypes.length > 1 &&
                      transferTypes.map((transferType, idx) => (
                        <button
                          key={idx}
                          className={`transfer-type ${transferType.type}${
                            transferType.type === selectedTransferType ? " selected" : ""
                          }`}
                          type="button"
                          onClick={handleChangeTransferType.bind(this, transferType.type)}
                          disabled={isSelectedTransferMethodInstant(transferType.type) && isSettingUpAstra}
                        >
                          <span className="icon-wrapper">
                            <transferType.Icon className="icon" />
                          </span>
                          <div className="title">{transferType.title}</div>
                          {isSelectedTransferMethodInstant(transferType.type) && isSettingUpAstra ? (
                            <CircularProgress />
                          ) : (
                            <span className="description">{transferType.description}</span>
                          )}
                          {isSelectedTransferMethodInstant(transferType.type) && isInstantTransferIneligible && (
                            <span className="restricted-badge">Restricted</span>
                          )}
                        </button>
                      ))}
                  </div>
                  <div className="details">
                    <div className="detail-row">
                      <span className="detail-label">Account</span>
                      {(isSelectedTransferMethodInstant(selectedTransferType) && !!selectedAstraCard) ||
                      (isSelectedTransferMethodBank(selectedTransferType) && !!selectedAccount) ? (
                        <span className="detail-value">
                          {isSelectedTransferMethodInstant(selectedTransferType)
                            ? instantAccountDescription
                            : accountDescription}
                          <button className="edit" type="button" onClick={handleSelectPaymentEntity}>
                            <Edit />
                          </button>
                        </span>
                      ) : (
                        <button className="detail-value" type="button" onClick={handleSelectPaymentEntity}>
                          {isSelectedTransferMethodInstant(selectedTransferType) ? (
                            <span className="color--primary-400">Link a Card</span>
                          ) : (
                            <span className="color--primary-400">Link a bank account</span>
                          )}
                          <span className="chevron">
                            <ChevronRight />
                          </span>
                        </button>
                      )}
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Fee</span>
                      <span className="detail-value">
                        {isSelectedTransferMethodInstant(selectedTransferType) ? feeLabel : "Free"}
                      </span>
                    </div>
                    {!isSelectedTransferMethodInstant(selectedTransferType) &&
                      !recurringDeposit?.data?.data?.active && (
                        <div className="detail-row">
                          <span className="detail-label">Make this recurring</span>
                          <FormSwitch checked={isRecurringTransaction} onClick={handleToggleRecurring} />
                        </div>
                      )}
                    <div className="detail-row">
                      <div className="info white-space-pre-line">
                        {isSelectedTransferMethodInstant(selectedTransferType)
                          ? "Transfer speed depends on your bank and could take up to 30 minutes."
                          : isRecurringTransaction
                            ? "Transfers take one business day to process.\nTransfers set up on a weekend, or holiday, will occur on the next business day."
                            : "2-3 business days. Transactions made after 3:00 PM ET or on a weekend or holiday will be processed the next business day."}
                      </div>
                    </div>
                  </div>
                  <PrimaryButton className="mt-sm-6 mt-4" type="submit" disabled={disableSubmitButton}>
                    {transferAmountLabel}
                  </PrimaryButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default withFormProvider(AddCash);
